import { TIME_RANGE_VALUES } from '@sweetistics/contracts';
import { z } from 'zod';

import type { TimeRange } from '@/types/timeline-analysis';

const DEFAULT_RANGE_MS: Record<string, number> = {
  '5m': 5 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '60m': 60 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '3h': 3 * 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '12h': 12 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '48h': 48 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '14d': 14 * 24 * 60 * 60 * 1000,
};

export const SUPPORTED_TIME_RANGES: readonly TimeRange[] = TIME_RANGE_VALUES;

const DEFAULT_TIME_RANGE_VALUES: readonly TimeRange[] = ['1h', '3h', '6h', '12h', '24h', '3d', '7d'];

const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  '1h': 'last hour',
  '3h': 'last 3 hours',
  '6h': 'last 6 hours',
  '12h': 'last 12 hours',
  '24h': 'last 24 hours',
  '3d': 'last 3 days',
  '7d': 'last 7 days',
};

const defaultLabel = TIME_RANGE_LABELS['24h'];

const TIME_RANGE_LABEL_ENTRIES = TIME_RANGE_VALUES.map((value) => [value, TIME_RANGE_LABELS[value]] as const);
const TIME_RANGE_LABEL_MAP = new Map<TimeRange, string>(TIME_RANGE_LABEL_ENTRIES);

const timeRangeSet = new Set<string>(SUPPORTED_TIME_RANGES);

export interface TimeRangeOption<T extends TimeRange = TimeRange> {
  readonly description: string;
  readonly label: string;
  readonly value: T;
}

export interface TimeRangeDisplayOption<T extends TimeRange = TimeRange> {
  readonly label: string;
  readonly value: T;
}

export function isTimeRange(value: unknown): value is TimeRange {
  return typeof value === 'string' && timeRangeSet.has(value);
}

export function getTimeRangeLabel(timeRange: TimeRange): string {
  return TIME_RANGE_LABEL_MAP.get(timeRange) ?? defaultLabel;
}

export function buildTimeRangeOptions<T extends TimeRange>(
  ranges?: readonly T[]
): readonly TimeRangeOption[] | readonly TimeRangeOption<T>[] {
  const resolved = ranges ?? DEFAULT_TIME_RANGE_VALUES;
  return resolved.map((range) => ({
    value: range,
    label: range,
    description: getTimeRangeLabel(range),
  }));
}

export const DEFAULT_TIME_RANGE_OPTIONS = buildTimeRangeOptions(DEFAULT_TIME_RANGE_VALUES);

function capitalizeFirstCharacter(value: string): string {
  if (value.length === 0) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildTimeRangeDisplayOptionsForRanges<T extends TimeRange>(
  ranges: readonly T[]
): readonly TimeRangeDisplayOption<T>[] {
  return ranges.map((range) => ({
    value: range,
    label: capitalizeFirstCharacter(getTimeRangeLabel(range)),
  }));
}

export function buildTimeRangeDisplayOptions<T extends TimeRange>(
  ranges: readonly T[]
): readonly TimeRangeDisplayOption<T>[];
// eslint-disable-next-line no-redeclare -- TS overload signature.
export function buildTimeRangeDisplayOptions(
  ranges: readonly TimeRange[] = DEFAULT_TIME_RANGE_VALUES
): readonly TimeRangeDisplayOption[] {
  return buildTimeRangeDisplayOptionsForRanges(ranges);
}

export const DEFAULT_TIME_RANGE_DISPLAY_OPTIONS = buildTimeRangeDisplayOptions(DEFAULT_TIME_RANGE_VALUES);

const RangeKeySchema = z.string().min(1);

export interface RelativeTimeWindow {
  end: Date;
  key: string;
  start: Date;
}

export interface ResolveRelativeTimeRangeOptions {
  /** Default range key when none is provided or parsing fails. Defaults to `24h`. */
  defaultRange?: string;
  /** Map of allowed keys to duration in milliseconds. */
  durations?: Record<string, number>;
  /** Reference "now" time; defaults to current time. */
  now?: Date;
  /** Optional range key to use instead of reading from search params (e.g. overriding with `days`). */
  overrideKey?: string | null;
  /** Input parameter name inside the search params. Defaults to `timeRange`. */
  param?: string;
}

function normaliseRangeKey(key: string): string {
  return key.trim().toLowerCase();
}

const RELATIVE_DURATION_PATTERN = /^(\d+)([dhm])$/;

function parseDurationMs(key: string, durations: Record<string, number>): number | null {
  const normalized = normaliseRangeKey(key);
  const predefined = durations[normalized];
  if (predefined) {
    return predefined;
  }

  const match = normalized.match(RELATIVE_DURATION_PATTERN);
  if (!match) {
    return null;
  }

  const valueRaw = match[1];
  const unit = match[2];
  if (!(valueRaw && unit)) {
    return null;
  }

  const value = Number.parseInt(valueRaw, 10);
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  switch (unit) {
    case 'm': {
      return value * 60 * 1000;
    }
    case 'h': {
      return value * 60 * 60 * 1000;
    }
    case 'd': {
      return value * 24 * 60 * 60 * 1000;
    }
    default: {
      return null;
    }
  }
}

function ensureRangeKey(key: string | null | undefined, fallback: string): string {
  if (!key) {
    return fallback;
  }
  const parsed = RangeKeySchema.safeParse(key);
  return parsed.success ? normaliseRangeKey(parsed.data) : fallback;
}

export function resolveRelativeTimeRangeFromParams(
  searchParameters: URLSearchParams,
  options: ResolveRelativeTimeRangeOptions = {}
): RelativeTimeWindow {
  const {
    overrideKey,
    param: parameter = 'timeRange',
    defaultRange = '24h',
    durations = DEFAULT_RANGE_MS,
    now = new Date(),
  } = options;

  const candidateKey = overrideKey ?? searchParameters.get(parameter);
  const rangeKey = ensureRangeKey(candidateKey, defaultRange);
  const durationMs = parseDurationMs(rangeKey, durations) ?? durations[defaultRange] ?? 24 * 60 * 60 * 1000;

  const end = new Date(now);
  const start = new Date(end.getTime() - durationMs);

  return { key: rangeKey, start, end };
}

export function resolveRelativeTimeRange(
  key: string | null | undefined,
  options: { durations?: Record<string, number>; now?: Date; defaultRange?: string } = {}
): RelativeTimeWindow {
  const { durations = DEFAULT_RANGE_MS, now = new Date(), defaultRange = '24h' } = options;
  const rangeKey = ensureRangeKey(key, defaultRange);
  const durationMs = parseDurationMs(rangeKey, durations) ?? durations[defaultRange] ?? 24 * 60 * 60 * 1000;

  const end = new Date(now);
  const start = new Date(end.getTime() - durationMs);

  return { key: rangeKey, start, end };
}

export { TimeRangeSchema as timeRangeSchema } from '@sweetistics/contracts';
